/**
 * One event: its layouts as drawing sheets (the plan of the hall with what each layout covers, and a
 * title block with its counts and who saved it last), making a new one empty or as a copy of any
 * layout, renaming, sharing, deleting and restoring.
 */

import type { EventRow, LayoutRow } from '@mawkeb/shared/api';
import type { MeasuredModel } from '@mawkeb/shared/model';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, hallModel } from '../../api';
import { navigate } from '../../router';
import { cacheGet, cacheKeys, cacheSet } from '../../state/cache';
import { deviceLayout, forgetDeviceLayout } from '../../state/layout';
import { useSession } from '../../state/session';
import { venue } from '../../state/store';
import { Dialog, Menu } from '../Dialog';
import { HallDrawing } from '../HallDrawing';
import { Back, Dots, Plus } from '../icons';
import { ShareDialog } from '../Share';
import { ago, days } from '../time';
import { EventDialog } from './Events';
import { Link, PageHeader, SavedOnDevice } from './Shell';

interface Page {
  event: EventRow;
  layouts: LayoutRow[];
  deleted: LayoutRow[];
}

function countsText(c: LayoutRow['counts']): [string, number][] {
  return [
    ['Chairs', c.chairs],
    ['Tables', c.tables],
    ['Banners', c.banners],
    ['People', c.people],
  ];
}

function NewLayout({ event, onClose }: { event: EventRow; onClose: () => void }) {
  const [name, setName] = useState('');
  const [from, setFrom] = useState<'empty' | 'copy'>('empty');
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [sourceEvent, setSourceEvent] = useState(event.id);
  const [layouts, setLayouts] = useState<LayoutRow[] | null>(null);
  const [source, setSource] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (from !== 'copy' || events) return;
    api.events().then((r) => setEvents(r.events), (e: Error) => setProblem(e.message));
  }, [from, events]);

  useEffect(() => {
    if (from !== 'copy') return;
    setLayouts(null);
    setSource('');
    api.event(sourceEvent).then((r) => {
      setLayouts(r.layouts);
      setSource(r.layouts[0]?.id ?? '');
    }, (e: Error) => setProblem(e.message));
  }, [from, sourceEvent]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      const r = await api.makeLayout(event.id, { name, ...(from === 'copy' && source ? { copyOf: source } : {}) });
      navigate(`/l/${r.layout.id}`);
    } catch (err) {
      setProblem((err as ApiError).status === 'offline' ? 'There’s no connection. Try again when you’re back online.' : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="New layout"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="layout-form" className="primary" disabled={busy || !name.trim() || (from === 'copy' && !source)}>
            Make the layout
          </button>
        </>
      }
    >
      <form id="layout-form" className="stack" onSubmit={submit}>
        <label className="input">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Main night, split hall" required autoFocus />
        </label>
        <fieldset className="choice">
          <legend>Start from</legend>
          <label>
            <input type="radio" name="from" checked={from === 'empty'} onChange={() => setFrom('empty')} /> The empty hall
          </label>
          <label>
            <input type="radio" name="from" checked={from === 'copy'} onChange={() => setFrom('copy')} /> A copy of another layout
          </label>
        </fieldset>
        {from === 'copy' && (
          <div className="two">
            <label className="input">
              <span>From the event</span>
              <select value={sourceEvent} onChange={(e) => setSourceEvent(e.target.value)}>
                {(events ?? [event]).map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="input">
              <span>The layout</span>
              <select value={source} onChange={(e) => setSource(e.target.value)} disabled={!layouts?.length}>
                {layouts?.length === 0 && <option value="">That event has no layouts</option>}
                {layouts?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        {problem && (
          <p className="form-problem" role="alert">
            {problem}
          </p>
        )}
      </form>
    </Dialog>
  );
}

function Rename({ layout, onClose, onDone }: { layout: LayoutRow; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(layout.name);
  const [problem, setProblem] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.changeLayout(layout.id, { name });
      onDone();
    } catch (err) {
      setProblem((err as Error).message);
    }
  };
  return (
    <Dialog
      title="Rename the layout"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="rename-form" className="primary" disabled={!name.trim()}>
            Rename
          </button>
        </>
      }
    >
      <form id="rename-form" className="stack" onSubmit={submit}>
        <label className="input">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required autoFocus />
        </label>
        {problem && <p className="form-problem">{problem}</p>}
      </form>
    </Dialog>
  );
}

function Sheet({ layout, model, unsaved, actions }: { layout: LayoutRow; model: MeasuredModel | null; unsaved: boolean; actions: { label: string; run: () => void; danger?: boolean }[] }) {
  return (
    <article className="sheet">
      <Link to={`/l/${layout.id}`} className="sheet-plan" aria-label={`Open ${layout.name}`}>
        <HallDrawing model={model} shapes={layout.preview} label={`Plan of ${layout.name}`} />
      </Link>
      <div className="title-block">
        <div className="tb-head">
          <h2 dir="auto">
            <Link to={`/l/${layout.id}`}>{layout.name}</Link>
          </h2>
          <Menu label={`${layout.name}: more`} icon={<Dots />} items={actions} />
        </div>
        {unsaved && <span className="tag">Unsaved changes on this device</span>}
        <dl className="tb-cells">
          {countsText(layout.counts).map(([label, n]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{n}</dd>
            </div>
          ))}
        </dl>
        <p className="tb-saved">
          Version {layout.version}, saved by {layout.savedBy.name} {ago(layout.savedAt)}
        </p>
      </div>
    </article>
  );
}

export function EventPage({ id }: { id: string }) {
  const me = useSession((s) => s.me);
  const admin = me?.role === 'admin';
  const [page, setPage] = useState<Page | null>(null);
  const [fromDevice, setFromDevice] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [model, setModel] = useState<MeasuredModel | null>(null);
  const [drafts, setDrafts] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<{ kind: 'new' } | { kind: 'rename' | 'share'; layout: LayoutRow } | { kind: 'event' } | null>(null);
  const [onDevice, setOnDevice] = useState(() => deviceLayout('zahraa-hall'));

  const load = useCallback(async () => {
    try {
      const r = await api.event(id);
      setPage(r);
      setFromDevice(null);
      setProblem(null);
      void cacheSet(`event:${id}`, { ...r, at: Date.now() });
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 401) return;
      const saved = await cacheGet<Page & { at: number }>(`event:${id}`);
      if (saved && err.status === 'offline') {
        setPage((p) => p ?? saved);
        setFromDevice(saved.at);
      } else setProblem(err.status === 'offline' ? 'There’s no connection, and this device hasn’t saved this event yet.' : err.message);
    }
    setDrafts(new Set((await cacheKeys('draft:')).map((k) => k.slice('draft:'.length))));
  }, [id]);

  useEffect(() => {
    void cacheGet<Page>(`event:${id}`).then((saved) => saved && setPage((p) => p ?? saved));
    void load();
  }, [id, load]);

  useEffect(() => {
    if (page) void hallModel(page.event.hall).then(setModel);
  }, [page?.event.hall]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (layout: LayoutRow) => {
    try {
      const r = await api.makeLayout(id, { name: `${layout.name} (copy)`.slice(0, 120), copyOf: layout.id });
      venue().toast(`Made a copy: ${r.layout.name}.`);
      void load();
    } catch (e) {
      venue().toast((e as Error).message, { tone: 'bad' });
    }
  };

  const remove = async (layout: LayoutRow) => {
    try {
      await api.deleteLayout(layout.id);
      venue().toast(`Deleted ${layout.name}.`, { action: { label: 'Undo', run: () => void api.restoreLayout(layout.id).then(load) } });
      void load();
    } catch (e) {
      venue().toast((e as Error).message, { tone: 'bad' });
    }
  };

  const restore = async (layout: LayoutRow) => {
    try {
      await api.restoreLayout(layout.id);
      venue().toast(`Restored ${layout.name}.`);
      void load();
    } catch (e) {
      venue().toast((e as Error).message, { tone: 'bad' });
    }
  };

  const bringIn = async () => {
    if (!onDevice) return;
    try {
      const r = await api.makeLayout(id, { name: 'Layout from this device', data: onDevice });
      forgetDeviceLayout('zahraa-hall');
      setOnDevice(null);
      navigate(`/l/${r.layout.id}`);
    } catch (e) {
      venue().toast((e as Error).message, { tone: 'bad' });
    }
  };

  const event = page?.event;
  return (
    <div className="page">
      <PageHeader />
      <main className="page-main" id="main">
        <Link to="/" className="back-link">
          <Back /> Events
        </Link>
        {problem && !page && (
          <div className="empty">
            <h2>That event couldn’t be opened</h2>
            <p>{problem}</p>
          </div>
        )}
        {event && (
          <>
            <div className="page-title">
              <div>
                <p className="kicker">{days(event.startsOn, event.endsOn) || 'Zahraa Hall'}</p>
                <h1 dir="auto">{event.name}</h1>
              </div>
              <div className="title-actions">
                {admin && (
                  <button type="button" className="secondary" onClick={() => setDialog({ kind: 'event' })}>
                    Rename or re-date
                  </button>
                )}
                <button type="button" className="primary with-icon" onClick={() => setDialog({ kind: 'new' })}>
                  <Plus /> New layout
                </button>
              </div>
            </div>
            <SavedOnDevice at={fromDevice} />
            {onDevice && (
              <div className="notice">
                <p>This device has a layout from before layouts were saved for the team.</p>
                <button type="button" className="secondary" onClick={() => void bringIn()}>
                  Bring it into this event
                </button>
              </div>
            )}
            {page.layouts.length === 0 ? (
              <div className="empty">
                <h2>No layouts yet</h2>
                <p>Start one from the empty hall, or copy a layout from another event.</p>
                <button type="button" className="primary" onClick={() => setDialog({ kind: 'new' })}>
                  New layout
                </button>
              </div>
            ) : (
              <div className="sheets">
                {page.layouts.map((l) => (
                  <Sheet
                    key={l.id}
                    layout={l}
                    model={model}
                    unsaved={drafts.has(l.id)}
                    actions={[
                      { label: 'Open', run: () => navigate(`/l/${l.id}`) },
                      { label: 'Rename', run: () => setDialog({ kind: 'rename', layout: l }) },
                      { label: 'Make a copy', run: () => void copy(l) },
                      { label: 'Share a view-only link', run: () => setDialog({ kind: 'share', layout: l }) },
                      { label: 'Delete', run: () => void remove(l), danger: true },
                    ]}
                  />
                ))}
              </div>
            )}
            {page.deleted.length > 0 && (
              <details className="deleted">
                <summary>Recently deleted ({page.deleted.length})</summary>
                <ul>
                  {page.deleted.map((l) => (
                    <li key={l.id}>
                      <span dir="auto">{l.name}</span>
                      <span className="quiet-text">deleted by {l.deletedBy?.name ?? 'someone'} {l.deletedAt ? ago(l.deletedAt) : ''}</span>
                      <button type="button" className="secondary small" onClick={() => void restore(l)}>
                        Restore
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </main>
      {dialog?.kind === 'new' && event && <NewLayout event={event} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'rename' && <Rename layout={dialog.layout} onClose={() => setDialog(null)} onDone={() => (setDialog(null), void load())} />}
      {dialog?.kind === 'share' && <ShareDialog layout={dialog.layout} onClose={() => setDialog(null)} onChanged={() => void load()} />}
      {dialog?.kind === 'event' && event && <EventDialog event={event} onClose={() => setDialog(null)} onDone={() => (setDialog(null), void load())} />}
    </div>
  );
}
